import { useState, useEffect, useMemo, useCallback } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import "@/components/dragndrop/formEditorStyles.css";
import { useNavigate, useParams } from "react-router-dom";
import { Column } from "@/components/dragndrop/dndColumn";
import CheckBox from "@/components/forms/inputs/checkBox";
import { IMeasurementDefinition } from "@/types/Entities";
import { useTranslation } from "react-i18next";
import { DotLoader } from "react-spinners";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import RestrictedPage from "@/consumer/restrictedPage";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "@/routes/admin/formEditor.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

interface MdCatalogue extends IMeasurementDefinition {
  mandatory?: boolean;
  visibility_ruleset?: any;
  order?: number;
  is_used_in_form?: any;
}

interface FormValue {
  name?: string;
}

export function FormEditor() {
  const [availableElements, setAvailableElements] = useState<MdCatalogue[]>([]);
  const [query, setQuery] = useState<string>("");
  const [rightColumn, setRightColumn] = useState<MdCatalogue[]>([]);
  const [leftColumn, setLeftColumn] = useState<MdCatalogue[]>([]);
  const [leftColumnPlaceholderText, setLeftColumnPlaceholderText] =
    useState<string>("");
  const [formName, setFormName] = useState<string>("");
  const [probeId, setProbeId] = useState<string>("");
  const [isEditableBySubject, setIsEditableBySubject] = useState<boolean>();
  const { handleError, errorModal } = useErrorModal();
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const [formValue, setFormValue] = useState<FormValue>({
    name: "",
  });
  const [showFieldPreview, setShowFieldPreview] = useState<boolean>(true);
  const [invalidRulesetItemId, setInvalidRulesetItemId] = useState<
    string | null
  >(null);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, string>({
      fieldOrder: [],
    });

  const { formId } = useParams() as { formId: string };
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { showConfirmationModalAsync } = useConfirmationModal();

  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
    setNotificationMessage("");
  };

  useEffect(() => {
    const fetchForm = async () => {
      if (!formId) {
        return;
      }
      try {
        const res: any = await new Promise((resolve, reject) => {
          api.genericApiRequest({
            entity: "form",
            method: "get",
            entityId: String(formId),
            successHandler: (res: any) => resolve(res),
            failHandler: (error: any) => reject(error),
          });
        });
        setProbeId(res.data.probe.id);
        setFormName(res.data.name);
        setIsEditableBySubject(res.data.editable_by_subject);
        const preexisting = res.data.measurementdefinitiontoform_set.map(
          (item: any) => ({
            ...item.measurement_definition,
            mandatory: item.mandatory,
            order: item.order,
            visibility_ruleset: item.visibility_ruleset,
          }),
        );
        const sorted = preexisting.sort((a: any, b: any) => a.order - b.order);
        setRightColumn(sorted);
      } catch (error) {
        handleError(error);
      }
    };
    fetchForm();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (query.trim().length >= 3) {
        setSearchLoading(true);
        const additionalUrlParameters: any = {};
        additionalUrlParameters["name__icontains"] = query;

        api.genericApiRequest({
          entity: "measurementDefinition",
          method: "get",
          getAllPages: true,
          parametersToRender: {
            depth: "0",
            limit: "50",
            additionalUrlParameters: additionalUrlParameters,
          },
          successHandler: (res: any) => {
            setAvailableElements(res.data.results);
            setSearchLoading(false);
            if (res.data.results.length === 0) {
              setLeftColumnPlaceholderText(t("no_results_found"));
            } else {
              setLeftColumnPlaceholderText(
                t("search_to_show_available_elements"),
              );
            }
          },
          failHandler: (error: any) => {
            handleError(error);
            setSearchLoading(false);
          },
        });
      } else {
        setAvailableElements([]);
        setLeftColumn([]);
        setLeftColumnPlaceholderText(t("search_to_show_available_elements"));
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  useEffect(() => {
    if (query.trim() !== "") {
      const filteredLeft = availableElements.filter(
        (item: any) =>
          !rightColumn.some((rightItem: any) => rightItem.id === item.id),
      );
      setLeftColumn(filteredLeft);
    }
  }, [availableElements, rightColumn, query]);

  //-- OnDrag - Start --
  function onDragEnd(result: any) {
    if (!result.destination) {
      return;
    }

    // reordering in same list
    if (result.source.droppableId === result.destination.droppableId) {
      if (result.source.droppableId !== "column-1") {
        return;
      }
      const items: any = reorderList(
        rightColumn,
        result.source.index,
        result.destination.index,
      );
      setRightColumn(items);
      return;
    }

    if (result.source.droppableId === "column-1") {
      return;
    }
    const item = leftColumn[result.source.index];
    const newItem = { mandatory: false, visibility_ruleset: "{}" };
    Object.assign(item, newItem);

    const newLeftColumn = [...leftColumn];
    newLeftColumn.splice(result.source.index, 1);

    const newRightColumn = [...rightColumn];
    newRightColumn.splice(result.destination.index, 0, item);

    setRightColumn(newRightColumn);
    setLeftColumn(newLeftColumn);
  }
  //-- OnDrag - End --
  const handleMandatoryCheckbox = useCallback(
    (id: any) => {
      const newRightColumn: any = [...rightColumn];
      const target = newRightColumn.find((item: any) => item.id === id);
      if (target) {
        target.mandatory = !target.mandatory;
      }
      setRightColumn(newRightColumn);
    },
    [rightColumn],
  );

  const handleMoveItem = useCallback(
    (index: number, direction: 1 | -1) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= rightColumn.length) {
        return;
      }
      setRightColumn(reorderList(rightColumn, index, targetIndex) as any);
    },
    [rightColumn],
  );

  const handleRulesetChange = useCallback(
    (value: any, id: any) => {
      const newRightColumn: any = [...rightColumn];
      const itemToUpdate = newRightColumn.find((item: any) => item.id === id);
      if (!itemToUpdate) {
        return;
      }
      itemToUpdate.visibility_ruleset = value;
      setRightColumn(newRightColumn);
      setInvalidRulesetItemId((currentId) =>
        currentId === String(id) ? null : currentId,
      );
    },
    [rightColumn],
  );

  const handleDelete = useCallback(
    async (idToDelete: any) => {
      const itemToDelete: any = rightColumn.find(
        (item: any) => item.id === idToDelete,
      );
      if (!itemToDelete) {
        return;
      }

      const isConfirmed = await showConfirmationModalAsync(
        t("form_delete_field_confirm", {
          name: itemToDelete?.label,
        }),
      );
      if (!isConfirmed) return;

      const newRightColumn = rightColumn.filter(
        (item: any) => item.id !== itemToDelete.id,
      );
      const newLeftColumn: any = availableElements.filter(
        (id1: any) => !newRightColumn.some((id2: any) => id1 === id2),
      );
      setRightColumn(newRightColumn);
      setLeftColumn(newLeftColumn);
    },
    [rightColumn, availableElements],
  );

  const handleSaveForm = async () => {
    const invalidRulesetItem: any = rightColumn.find((item: any) => {
      const ruleset = item.visibility_ruleset;
      if (typeof ruleset !== "string" || ruleset.trim() === "") {
        return false;
      }
      try {
        JSON.parse(ruleset);
        return false;
      } catch {
        return true;
      }
    });
    if (invalidRulesetItem) {
      setInvalidRulesetItemId(String(invalidRulesetItem.id));
      setNotificationMessage(
        t("visibility_ruleset_invalid_json", { name: invalidRulesetItem.label }),
      );
      setNotificationVariant("error");
      setShowNotification(true);
      return;
    }
    setInvalidRulesetItemId(null);

    const updatedRightColumn = rightColumn.map((item: any) => {
      const visibilityRulesetString =
        typeof item.visibility_ruleset === "string"
          ? item.visibility_ruleset.trim()
          : JSON.stringify(item.visibility_ruleset);
      return visibilityRulesetString !== "{}"
        ? { ...item, mandatory: false }
        : item;
    });
    const result: any = updatedRightColumn.map((item: any) => ({
      id: item.id,
      mandatory: item.mandatory,
      visibility_ruleset:
        item.visibility_ruleset !== "" ? item.visibility_ruleset : "{}",
    }));

    setRightColumn(updatedRightColumn);

    const submitData = {
      name: formValue.name !== "" ? formValue.name : formName,
      probe: probeId,
      measurement_definition: result,
      editable_by_subject: isEditableBySubject,
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    try {
      const response = await new Promise<any>((resolve, reject) => {
        api.genericApiRequest({
          entity: "form",
          method: "put",
          successHandler: (res: any) => {
            resolve(res);
          },
          failHandler: (error: any) => {
            reject(error);
          },
          entityId: String(formId),
          submitData: submitData,
          config: config,
        });
      });
      setNotificationMessage(t("save_successful"));
      setNotificationVariant("success");
      setShowNotification(true);
      setProbeId(response.data.probe?.id);
      navigate(`/settings/formEditor/${response.data.id}`);
    } catch (error) {
      handleError(error);
    }
  };

  const handleNavigateBack = () => {
    if (probeId !== "") {
      navigate(`/settings/probeEditor/${probeId}`);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (!invalidRulesetItemId) return;
    const focusTimer = window.setTimeout(() => {
      focusFirstInvalidField({}, [invalidRulesetItemId]);
    }, 0);
    return () => window.clearTimeout(focusTimer);
  }, [focusFirstInvalidField, invalidRulesetItemId]);

  function reorderList(list: any, startIndex: any, endIndex: any) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  }

  const handleChange = (event: any) => {
    setFormValue({ ...formValue, [event.target.name]: event.target.value });
  };

  const handleCheckboxChange = (value: boolean) => {
    setIsEditableBySubject(value);
  };

  const memoizedLeftColumn = useMemo(
    () => (
      <Grid item xs={4} className={styles.columnGridItem}>
        {searchLoading ? (
          <div className={styles.searchLoadingWrapper}>
            <DotLoader color="#8c1ec8" size={40} />
          </div>
        ) : (
          <Column
            key={"column-0"}
            column={{
              id: "column-0",
              items: leftColumn,
              title: t("available_elements"),
            }}
            index={0}
            leftColumnPlaceholderText={leftColumnPlaceholderText}
          />
        )}
      </Grid>
    ),
    [leftColumn, searchLoading, leftColumnPlaceholderText],
  );

  const rulesetKeySuggestions = useMemo(
    () =>
      rightColumn.map((item: any) => ({
        value: item.name,
        label: item.label,
      })),
    [rightColumn],
  );

  const memoizedRightColumn = useMemo(
    () => (
      <Grid item xs={8} className={styles.columnGridItem}>
        <Column
          key={"column-1"}
          column={{
            id: "column-1",
            items: rightColumn,
            title: `${t("form_fields")} (${rightColumn.length})`,
          }}
          index={1}
          handleDeleteItem={handleDelete}
          onHandleCheckboxChange={handleMandatoryCheckbox}
          onHandleRulesetChange={handleRulesetChange}
          onMoveItem={handleMoveItem}
          showFieldPreview={showFieldPreview}
          keySuggestions={rulesetKeySuggestions}
          invalidRulesetItemId={invalidRulesetItemId}
          registerRulesetValidationTarget={registerValidationTarget}
        />
      </Grid>
    ),
    [
      rightColumn,
      handleDelete,
      handleMandatoryCheckbox,
      handleRulesetChange,
      handleMoveItem,
      showFieldPreview,
      rulesetKeySuggestions,
      invalidRulesetItemId,
      registerValidationTarget,
    ],
  );

  const rulesetExamples = [
    { json: `{"${t("variable_name")}": {"is": "yes"}}` },
    { json: `{"${t("variable_name")}": {"not": "no"}}` },
    { json: `{"${t("variable_name")}": {"gt": 0.5}}` },
    { json: `{"${t("variable_name")}": {"lt": 1.4}}` },
    { json: `{"${t("variable_name")}": {"in": [0.1, 0.2, 5.0]}}` },
    {
      json: `{"${t("variable_name")}": {"nin": ["${t("hay_fever")}", "${t(
        "rash",
      )}"]}}`,
    },
    {
      prefix: t("for_multiple_selection"),
      json: `{"${t("variable_name")}": {"contains": "${t("hay_fever")}"}}`,
    },
    {
      prefix: t("for_multiple_selection"),
      json: `{"${t("variable_name")}": {"contains": ["${t("hay_fever")}", "${t(
        "rash",
      )}"]}}`,
    },
  ];

  const handleCopyExample = (json: string) => {
    navigator.clipboard.writeText(json);
    setNotificationMessage(t("copied"));
    setNotificationVariant("success");
    setShowNotification(true);
  };

  return (
    <RestrictedPage permissionRequired="frontend_view_form_editor">
      <>
        <DragDropContext onDragEnd={onDragEnd}>
          <div className={styles.editorLayout}>
            <Accordion
              disableGutters
              elevation={0}
              className={styles.examplesAccordion}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={styles.examplesSummary}
              >
                {t("visibility_ruleset_examples")}
              </AccordionSummary>
              <AccordionDetails className={styles.examplesDetails}>
                {rulesetExamples.map((example) => (
                  <Tooltip
                    key={example.json}
                    arrow
                    placement="top"
                    title={t("copy_json")}
                  >
                    <code
                      className={styles.exampleChip}
                      onClick={() => handleCopyExample(example.json)}
                    >
                      {example.prefix
                        ? `${example.prefix}: ${example.json}`
                        : example.json}
                    </code>
                  </Tooltip>
                ))}
              </AccordionDetails>
            </Accordion>
            <div className={styles.stickyToolbar}>
              <div className={styles.toolbarSearch}>
                <DefaultInput
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("search_placeholder")}
                />
              </div>
              <div className={styles.toolbarName}>
                {formName && (
                  <DefaultInput
                    name="name"
                    onChange={handleChange}
                    defaultValue={formName}
                  />
                )}
              </div>
              <CheckBox
                label={t("fillable_by_portal_user")}
                onChange={handleCheckboxChange}
                defaultValue={isEditableBySubject}
                labelClassName={styles.checkBoxLabel}
              />
              <FormControlLabel
                className={styles.previewToggle}
                control={
                  <Switch
                    checked={showFieldPreview}
                    onChange={(e) => setShowFieldPreview(e.target.checked)}
                    size="small"
                  />
                }
                label={t("show_field_preview")}
              />
              <div className={styles.toolbarActions}>
                <GenericButton variant="outlined" onClick={handleNavigateBack}>
                  {t("back")}
                </GenericButton>
                <GenericButton
                  waitingForResponse={true}
                  showSpinnerOnDisable={true}
                  onClick={handleSaveForm}
                >
                  {t("save")}
                </GenericButton>
              </div>
            </div>
            <Paper className={styles.container3}>
              <Droppable
                droppableId="all-droppables"
                direction="horizontal"
                type="column"
              >
                {(provided) => (
                  <Grid
                    container
                    columnSpacing={"1rem"}
                    component={"div"}
                    className={styles.columnContainer}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {memoizedLeftColumn}
                    {memoizedRightColumn}
                    {provided.placeholder}
                  </Grid>
                )}
              </Droppable>
            </Paper>
          </div>

          <>
            {errorModal}
            <GenericNotification
              message={showNotification ? notificationMessage : null}
              variant={notificationVariant}
              handleCloseSnackbar={resetStateOnCloseNotification}
            />
          </>
        </DragDropContext>
      </>
    </RestrictedPage>
  );
}
